<template>
  <div>
    <div id="non-printable">
      <LoadingScreen
        v-if="
          !Object.keys(this.$root.$data.doneLoading).every((key) => {
            return this.$root.$data.doneLoading[key];
          })
        "
      ></LoadingScreen>
      <!-- button navigation -->
      <div class="locale-button">
        <locale-button :home="true" :displayRestart="displayRestart" @restart="restart" />
      </div>

      <VitalSignCamera
        ref="vscam"
        class="camera"
        :isActive="starting"
        :userInfo="userInfo"
        :config="config"
        :visualizationOptions="visualizationOptions"
        :enableAgeEstimation="true"
        :device="selectedDevice"
        @onVideoFrameProcessed="onVideoFrameProcessed"
        @onCameraDevicesUpdated="onCameraDevicesUpdated"
        @onInitialized="onInitialized"
        @onError="onError"
      />
      <img
        src="./assets/silhouette.png"
        alt=""
        class="silhouette"
        v-if="modalState == 'measuring' || modalState == 'condition-check'"
      />
      <div class="position-relative">
        <!-- popup user profile -->
        <user-profile-input
          v-if="modalState == 'user-profile-input'"
          :form="form"
          @updateProfile="updateProfile"
          @updateModalState="updateModalState"
        />
        <!-- video container -->
        <div v-if="modalState == 'face-lost'" class="instruction-text">
          <p class="w-75">
            Face Lost!
            <br />
            Please keep your face visible to the camera
          </p>
        </div>

        <start-content
          v-if="modalState == 'start-content' || modalState == 'tnc'"
          :modalState="modalState"
          @startMeasurement="startMeasurement"
          @removeProfile="removeProfile"
          @updateModalState="updateModalState"
        />

        <condition-checking
          v-if="modalState == 'condition-check'"
          :conditions="conditions"
          :countdown="countdown"
          :startCountdown="startCountdown"
        />

        <circular-progress :timeLeft="timeLeft" v-if="modalState == 'measuring'" />
        <!-- 
        <img
          v-if="devices && devices.length > 1 && vsStatus == 'idle' && starting && !showModal"
          :disabled="vitalSign !== undefined"
          class="btn camera-switch"
          @click="switchCamera"
          src="./assets/switch-camera.svg"
        /> -->

        <result
          v-if="modalState == 'result'"
          :vitalSign="vitalSign"
          :userProfile="form"
          :recommendation="recommendation"
          @removeProfile="removeProfile"
        />

        <restart-modal v-if="displayRestartModal" @closeRestart="closeRestart" @confirmRestart="confirmRestart" />
      </div>
    </div>
    <div id="printable" v-if="vitalSign && form">
      <report :userProfile="form" :vitalSign="vitalSign" @updateRecommendation="updateRecommendation" />
    </div>
  </div>
</template>

<script>
import Logger from "../../common/logger.js";
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import LoadingScreen from "./loading-screen.vue";
import UserProfileInput from "./user-profile-input.vue";
import { newUserManager } from "../../user/company-user-manager.ts";
import Result from "./result/result.vue";
import StartContent from "./start-content.vue";
import LocaleButton from "../sidebar/locale-button.vue";
import ConditionChecking from "./condition-checking.vue";
import CircularProgress from "./circular-progress.vue";
import { resetMovementArray } from "@/plugin/plugin_helpers.js";
import Report from "./result/report.vue";
import RestartModal from "./restart-modal.vue";
import { ServerId, Gender, VitalSignCamera, GetHealthStage } from "vue-vital-sign-camera";

export default {
  name: "App",
  props: {
    simulation: Boolean,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();

    var currentUser = newUserManager.currentUser ? newUserManager.currentUser.currentUser : null;
    let vitalSign = null;
    if (newUserManager.currentUser && newUserManager.currentUser.cloudInfo) {
      vitalSign = currentUser.lastVitalSigns;
    } else {
      router.push("/");
    }

    return {
      t,
      locale,
      router,
      starting: ref(false),
      vitalSign: ref(vitalSign),
      conditions: ref(),
      timeLeft: ref(null),
      user: ref(currentUser),
    };
  },
  mounted() {
    setTimeout(async () => {
      if (this.user) {
        this.form = await this.user.getUserSettings();
      }
    }, 50);
  },
  async created() {
    await this.getUserType();
    if (window.sessionStorage.userSettings) {
      this.form = JSON.parse(window.sessionStorage.userSettings);
    }
    if (this.$root.$data.debugMode) {
      let currentUser = newUserManager.currentUser ? newUserManager.currentUser.currentUser : null;
      if (currentUser && !currentUser.lastHealth) {
        if (!currentUser.email) {
          currentUser._genUserInfo();
          Logger.log("%c<email-page debug>generated user info", "color:magenta");
          this.form = currentUser.userSettings;
        }
        if (!currentUser.lastHealth) {
          currentUser._genLastHealth();
          Logger.log("%c<email-page debug>generated last health", "color:magenta");
          this.vitalSign = currentUser.lastVitalSigns;
        }
        Logger.log("%cgenerated currentUser: ", "color:magenta", currentUser);
      }
    }
  },
  watch: {
    healthResult: {
      deep: true,
      handler: function (newValue) {
        this.finishMeasure();
        newUserManager.currentUser.currentUser.setHealth(newValue);
        this.vitalSign = newUserManager.currentUser.currentUser.lastVitalSigns;
      },
    },
    conditions() {
      if (this.conditions) {
        this.okToScan = Object.values(this.conditions).every((item) => item);
        if (this.okToScan && !this.startCountdown && this.modalState == "condition-check") {
          this.startCountdown = true;
          this.countdown = 3;
          this.interval = setInterval(() => {
            this.countdown -= 1;
            if (this.countdown == 0) {
              this.startCountdown = false;
              this.startMeasureTime = new Date();
              this.$refs.vscam.startScanning();
              clearInterval(this.interval);
              this.countdown = 3;
              this.interval = undefined;
              this.modalState = "measuring";
            }
            if (!this.okToScan) {
              this.startCountdown = false;
              clearInterval(this.interval);
              this.countdown = 3;
              this.interval = undefined;
            }
          }, 1000);
        }
      }
    },
  },
  data() {
    return {
      modalState: "start-content",
      form: undefined,
      userType: undefined,
      result: undefined,
      cameraIndex: 0,
      devices: [{ text: "", id: "", deviceIndex: 0 }],
      countdown: 3,
      resetTimeout: undefined,
      startCountdown: false,
      recommendation: undefined,
      displayRestartModal: false,
      config: {
        apiKey: "5MJoAQeSyOazZmSvKyyro45IfnUryvIh4ekOXYax",
        serverId: ServerId.AwsProdEnterprise,
        enableChestROI: true,
      },
      selectedDevice: undefined,
      visualizationOptions: {
        faceMesh: {
          color: "rgba(144, 238, 144, 0.3)",
          enabled: this.faceMeshEnabled,
          lineWidth: "0.4%",
          animate: true,
        },
      },
      userInfo: {},
      interval: undefined,
      okToScan: false,
      healthResult: undefined,
    };
  },
  methods: {
    updateModalState(state) {
      this.modalState = state;
    },
    onError(error) {
      if (error.message.includes("face lost")) {
        this.modalState = "face-lost";
      }
      setTimeout(() => {
        this.starting = false;
        this.removeProfile();
      }, 5000);
    },
    onVideoFrameProcessed(event) {
      const { scanConditions } = event;
      this.conditions = scanConditions;
      this.timeLeft = event.healthResult.remainingTime;
      if (this.timeLeft == 0 && this.modalState == "measuring") {
        if (this.$root.$data.doneLoading.faceDetectionView) {
          this.$root.$data.doneLoading.faceDetectionView = false;
        }
      } else {
        if (!this.$root.$data.doneLoading.faceDetectionView) {
          this.$root.$data.doneLoading.faceDetectionView = true;
        }
      }
      if (event.healthResult.health != undefined) {
        this.healthResult = event.healthResult.health;
      }
      if (event.healthResult.stage === GetHealthStage.AnalyzingData) {
        this.modalState = "analyzing";
        this.$root.$data.doneLoading.scanning = false;
      }
    },
    onCameraDevicesUpdated(event) {
      this.devices = event.devices;
      if (event.selected && event.selected !== "") {
        this.selectedDevice = event.selected;
      }
    },
    onInitialized() {
      this.$root.$data.doneLoading.faceDetectionView = true;
    },
    restart() {
      this.displayRestartModal = true;
    },
    closeRestart() {
      this.displayRestartModal = false;
    },
    confirmRestart() {
      this.displayRestartModal = false;
      this.removeProfile();
      if (this.modalState == "measuring") {
        this.$refs.vscam.stopScanning();
      }
      if (this.startCountdown && this.interval) {
        this.startCountdown = false;
        clearInterval(this.interval);
        this.countdown = 3;
        this.interval = undefined;
      }
      if (this.starting) {
        this.starting = false;
      }
    },
    updateRecommendation(value) {
      this.recommendation = value;
    },
    switchCamera() {
      this.cameraIndex = (this.cameraIndex + 1) % this.devices.length;
    },
    async getUserType() {
      setTimeout(() => {
        if (!this.currentUser) return;
        else {
          this.userType = this.currentUser.userSettings.planType;
        }
      }, 2000);
    },
    startMeasurement() {
      // check conditions
      this.starting = true;
      this.vitalSign = null;
      if (newUserManager.currentUser) {
        let currentUser = newUserManager.currentUser.currentUser;
        if (currentUser) {
          currentUser.clearHealth();
        }
      }
    },
    finishMeasure() {
      this.modalState = "result";
      this.$root.$data.doneLoading.scanning = true;
      resetMovementArray();
      this.starting = false;
      this.resetTimeout = setTimeout(() => {
        this.removeProfile();
      }, 5 * 1000 * 60);
    },
    fullScreen() {
      this.$refs.videoContainer.requestFullscreen();
    },
    async removeProfile() {
      await newUserManager.currentUser.currentUser.clearUserSettings();
      this.form = newUserManager.currentUser.currentUser.userSettings;
      this.vitalSign = null;
      if (this.modalState !== "start-content") {
        this.modalState = "start-content";
      }
      if (this.resetTimeout) {
        clearTimeout(this.resetTimeout);
        this.resetTimeout = undefined;
      }
    },
    async updateProfile(form) {
      await newUserManager.currentUser.currentUser.setUserSettings(form);
      this.form = newUserManager.currentUser.currentUser.userSettings;
      this.showTC = true;
      this.userInfo = {
        ...form,
        userId: newUserManager.currentUser.UID,
        gender: Gender.Male,
        age: 30,
      };
    },
  },
  computed: {
    displayRestart() {
      if (this.modalState == "start-content") {
        return false;
      } else {
        return true;
      }
    },
  },
  components: {
    UserProfileInput,
    Result,
    StartContent,
    LocaleButton,
    ConditionChecking,
    CircularProgress,
    Report,
    LoadingScreen,
    RestartModal,
    VitalSignCamera,
  },
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;800&display=swap");

html {
  scroll-behavior: smooth;
}
body {
  background: url("./assets/background-image.svg");
  background-size: cover;
  width: 100vw !important;
}

@media print {
  body {
    background: white;
    max-height: 70vh !important;
    min-height: 70vh !important;
  }
}
</style>

<style scoped>
#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.camera {
  position: absolute;
  -webkit-transform: v-bind(transform);
  transform: v-bind(transform);
  object-fit: cover;
  width: 100%;
  height: 100%;
  top: 0;
  z-index: -1;
  overflow: hidden;
}
.camera-switch {
  transform: scale(0.8);
  position: absolute;
  bottom: 5%;
  right: 3%;
}
.instruction-text {
  display: flex;
  justify-content: center;
  font-size: 30px;
  color: white;
  text-shadow: 0px 0px 5px black;
  z-index: 3;
  text-align: center;
  pointer-events: none;
  margin-top: 80px;
}
.silhouette {
  position: absolute;
  top: 0%;
  left: 0%;
  -webkit-transform: v-bind(transform);
  transform: v-bind(transform);
  object-fit: cover;
  width: 100%;
  height: 100vh;
  z-index: 0;
}
.locale-button {
  z-index: 1;
  position: relative;
  width: 100%;
}

@media print {
  #non-printable {
    display: none;
  }
  #printable {
    display: block;
  }
}
</style>
